import { useState } from 'react'
import type { KeyboardEvent } from 'react'

import type { SerializedStyles } from '@emotion/react'
import { useRouter } from 'next/router'
import { useDebouncedCallback } from 'use-debounce'

import type { Article, SearchArticlesList } from '@twisto/api-cms'
import { SearchBar } from '@twisto/components/molecules/search-bar'
import { normalizeText } from '@twisto/utils'

import { searchArticles } from 'homepage/utils/api'

import { ArticleSearchResults } from './article-search-results'
import * as styles from './articles-search-input.styles'

const DELAY = 500
const MINIMUM_QUERY_LENGTH = 2
const SUGGESTIONS_LIMIT = 4

type Props = {
  rootCategoryId?: Article['rootCategoryId']
  className?: string
  placeholder?: string
  dropdownClassName?: SerializedStyles
}

export const ArticlesSearchInput = ({
  className,
  placeholder,
  rootCategoryId,
  dropdownClassName,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const router = useRouter()
  const [focusedSearchInput, setFocusedSearchInput] = useState(false)
  const [articles, setArticles] = useState<SearchArticlesList>([])
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [dropdownOpened, setDropdownOpened] = useState(false)

  const articlesDebounced = useDebouncedCallback(async (query) => {
    const articleResponse = await searchArticles(
      normalizeText(query),
      SUGGESTIONS_LIMIT,
      rootCategoryId
    )
    setDropdownOpened(true)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    setArticles(articleResponse.articles?.data)
  }, DELAY)

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.length >= MINIMUM_QUERY_LENGTH) {
      articlesDebounced(query)
    } else {
      setDropdownOpened(false)
      setArticles([])
    }
    setHighlightedIndex(0)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        setHighlightedIndex((prevIndex) =>
          prevIndex < articles.length - 1 ? prevIndex + 1 : prevIndex
        )
        break
      case 'ArrowUp':
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : prevIndex
        )
        break
      case 'Enter':
        router.push({
          pathname: articles[highlightedIndex].path,
        })
        break
      default:
        break
    }
  }

  return (
    <div
      className={className}
      css={styles.searchInputWrapper}
      onBlur={(event) => {
        // prevent closing when click on dropdown list
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocusedSearchInput(false)
        }
      }}
      onFocus={() => setFocusedSearchInput(true)}
    >
      <SearchBar
        autocomplete="off"
        name="article-search"
        placeholder={placeholder}
        searchQuery={searchQuery}
        setSearchQuery={handleSearch}
        onKeyDown={handleKeyDown}
      />
      <ArticleSearchResults
        articles={articles}
        css={dropdownClassName}
        focusedIndex={highlightedIndex}
        opened={focusedSearchInput && dropdownOpened}
      />
    </div>
  )
}
